import { Action } from 'redux';
import { select, put } from 'redux-saga/effects';

import { AppState, ErrorAction } from 'store';
import { PaymentDetailsModel, CardPaymentStateModel, IfcPaymentStateModel, PaymentTypes } from 'models/Payment';
import { PaymentActionTypes, PaymentActions } from '../';
import { api } from 'repositories/api';
import { cardPaymentHelper, userHelper, ifcPaymentHelper, errorToBasketErrorModel } from 'helpers';
import { loadPaymentSaga } from './loadPayment';
import { FinanceApplicationStatusModel } from 'models';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

export interface InitializePaymentCalculatorAction extends Action<PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR> {
}

export interface InitializePaymentCalculatorActionStart extends Action<PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_START> {
}

export interface InitializePaymentCalculatorActionSuccess extends Action<PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_SUCCESS> {
    paymentDetails: PaymentDetailsModel;
    cardPaymentState: CardPaymentStateModel;
    ifcPaymentState: IfcPaymentStateModel;
    isIfcFormSubmitted: boolean;
}

export interface InitializePaymentCalculatorActionError extends ErrorAction<PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_ERROR> {
}

export function* initializePaymentCalculatorSaga(): BasketSagaReturnType {
    yield put<PaymentActions>({
        type: PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_START,
    });

    try {
        let state: AppState = yield select();
        let sessionId = userHelper.getSessionId();
        let username = userHelper.getCurrentUserName();
        const basket = state.basketState.basket!;
        const basketId = basket.guid;

        yield callBasketSaga(loadPaymentSaga, {
            type: PaymentActionTypes.LOAD_PAYMENT
        });

        state = yield select();
        let paymentDetails = state.paymentState.paymentDetails;

        let totalPrice = state.basketState.basket!.totalPriceWithTax.value;

        let ifcConfig = yield api.ifcConfig.getIfcCalculatorConfig(totalPrice);

        let cardPaymentState = cardPaymentHelper.initializePayment(
            totalPrice,
            paymentDetails,
            basket.hasDwellProducts());

        let ifcPaymentState = ifcPaymentHelper.initializeIfc(
            paymentDetails,
            state.basketState.basket!,
            ifcConfig);

        let paymentType = paymentDetails.isEmptyModel ? PaymentTypes.None : paymentDetails.paymentType;

        if (basket.hasDwellProducts()) {
            paymentType = PaymentTypes.Card;
        }

        let updatedPaymentDetails = {
            paymentType,
            cashCardDeposit: cardPaymentState.deposit,
            cashCardPercentage: cardPaymentState.percentage,
            ifcDeposit: ifcPaymentState.deposit,
            ifcDepositPercentage: ifcPaymentState.depositPercentage,
            ifcMonthlyAmount: ifcPaymentState.monthlyPayment,
            ifcNumMonths: ifcPaymentState.months,
            total: totalPrice,
            isEmptyModel: false
        };

        const applicationStatus: FinanceApplicationStatusModel | null = yield api.hybris.getFinanceApplicationStatus(username, basketId);
        const isIfcFormSubmitted = applicationStatus !== null;

        // If customer returned to basket page from deposit payment page (customer has submitted finance application in IFC flow)
        // we should disable IFC payment option on UI and set selected payment type to "None"
        // but we should not save "None" type to backend until customer select any other payment type
        // because customer still can return to deposit payment page (with support help)
        // and customer should be able to continue IFC flow
        // BUT if any other payment data was changed (total price, deposit, etc.) - then continuing IFC flow (from deposit page) is prohibited - and we save to backend "None" payment type
        // eslint-disable-next-line sonarjs/no-collapsible-if
        if (updatedPaymentDetails.paymentType === PaymentTypes.IFC && !ifcPaymentHelper.isIfcPaymentAvailable(basket, isIfcFormSubmitted, ifcPaymentState)) {
            if (!isIfcFormSubmitted || isIfcPaymentDetailsChanged(paymentDetails, updatedPaymentDetails)) {
                updatedPaymentDetails.paymentType = PaymentTypes.None;
            }
        }

        yield api.hybris.savePaymentDetails(username, sessionId!, updatedPaymentDetails);

        if (updatedPaymentDetails.paymentType === PaymentTypes.IFC && !ifcPaymentHelper.isIfcPaymentAvailable(basket, isIfcFormSubmitted, ifcPaymentState)) {
            updatedPaymentDetails.paymentType = PaymentTypes.None;
        }

        yield put<PaymentActions>({
            type: PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_SUCCESS,
            paymentDetails: updatedPaymentDetails,
            cardPaymentState,
            ifcPaymentState,
            isIfcFormSubmitted
        });

        return true;
    } catch (error) {
        yield put<PaymentActions>({
            type: PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR_ERROR,
            error: errorToBasketErrorModel(error, 'initializePaymentCalculatorSaga')
        });

        return false;
    }
}

function isIfcPaymentDetailsChanged(originalPaymentDetails: PaymentDetailsModel, updatedPaymentDetails: PaymentDetailsModel): boolean {
    return originalPaymentDetails.ifcDeposit !== updatedPaymentDetails.ifcDeposit
        || originalPaymentDetails.ifcDepositPercentage !== updatedPaymentDetails.ifcDepositPercentage
        || originalPaymentDetails.ifcMonthlyAmount !== updatedPaymentDetails.ifcMonthlyAmount
        || originalPaymentDetails.ifcNumMonths !== updatedPaymentDetails.ifcNumMonths
        || originalPaymentDetails.total !== updatedPaymentDetails.total;
}